import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Trans, withI18n } from '@lingui/react'
import urlFor, { QueryParams } from 'shared/tools/url-helper'
import isEmpty from 'lodash/isEmpty'
import compose from 'lodash/fp/compose'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary from 'client/shared/decorators/error-boundary'

import {
  loadGroupedQuotes,
  buildUser,
} from 'client/bookmate/reducers/user-reducer'

import { getGroupedQuotes } from 'client/bookmate/reducers/quotes-reducer'

import UserSubHeaderBox from 'client/bookmate/boxes/user-sub-header-box'
import UserSubHeaderBooksBox from 'client/bookmate/boxes/user-sub-header-books-box'
import HeaderBox from 'client/shared/boxes/header-box'
import UserInfoBox from 'client/bookmate/boxes/user-info-box'
import { CardQuoteBox } from 'client/bookmate/boxes/card-quote-box'
import List from 'client/bookmate/blocks/list'
import MoreButton from 'client/shared/blocks/more-button'

import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import Spacer from 'client/shared/blocks/spacer'
import { Dispatch, State } from 'shared/types/redux'
import { CurrentUserData } from 'client/shared/types/current-user'

const getQuotesContent = compose(join(', '), map('quote.content'))

type Props = {
  dispatch: Dispatch
  user: CurrentUserData
  query?: QueryParams
}

class UserQuotesBox extends Component<Props> {
  load = () => {
    const {
      dispatch,
      user: { login },
      groupedQuotesPage,
    } = this.props

    return login
      ? dispatch(loadGroupedQuotes(login, groupedQuotesPage, 20, true))
      : undefined
  }

  render() {
    const {
      user,
      user: { myself, login },
      groupedQuotes,
      groupedQuotesPage,
    } = this.props

    return (
      <>
        {this.renderMeta()}
        <HeaderBox />
        {myself ? <UserSubHeaderBooksBox /> : <UserInfoBox user={user} />}
        <UserSubHeaderBox hidden={myself} />
        <Spacer size={32} />
        {!isEmpty(groupedQuotes) && (
          <LayoutColumn>
            <InfiniteScroll load={this.load} page={groupedQuotesPage}>
              <List>
                {groupedQuotes.map((item, i) =>
                  this.renderGroup(item, i, login),
                )}
              </List>
            </InfiniteScroll>
          </LayoutColumn>
        )}
        <Spacer size="bottom-page" />
      </>
    )
  }

  renderMeta() {
    const {
      user: { name = '', login: username = '' },
      i18n,
      groupedQuotes,
    } = this.props
    const title = i18n._('profile.meta_title_quotes', { name, username })
    const description = getQuotesContent(groupedQuotes)

    return (
      <>
        <MetaTitle title={title} />
        <MetaDescription description={description} />
      </>
    )
  }

  renderGroup(group, key, login) {
    const count = group.count - 1
    const {
      quote,
      quote: { book },
    } = group

    const bookUuid =
      book.source_type === 'serial_episode' ? book.parent_uuid : book.uuid

    return (
      <div key={key}>
        <CardQuoteBox kind="default" quote={quote} embeddedBook={book} />
        {Boolean(count) && (
          <MoreButton
            path={urlFor.userBookQuotes(
              login,
              bookUuid,
              book.source_type === 'serial_episode',
              this.props.query,
            )}
            centered
          >
            <Trans id="feed.more_quotes" values={{ count }} />
          </MoreButton>
        )}
        <Spacer size={24} />
      </div>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { username } = params
    const { dispatch } = store

    const load = [dispatch(loadGroupedQuotes(username))]
    await Promise.all(load)
  },
  {
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  user: buildUser(state, state.user.login),
  groupedQuotes: getGroupedQuotes(state, state.user.grouped_quotes),
  groupedQuotesPage: state.user.pages.grouped_quotes,
  query: state.app.storedQuery,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(UserQuotesBox)
